import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import CategoryIcon from "@mui/icons-material/Category";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PersonIcon from "@mui/icons-material/Person";
import ViolationComments from "../OwnerDashboard/Actions/ViolationComments";
import ViolationsTimeline from "../OwnerDashboard/Actions/ViolationsTimeline";
import Visits from "../Visits";
import api, { API_BASE_URL } from "../../utils/api";

const MemberViolationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [violationDetails, setViolationDetails] = useState(null);
  const [violationType, setViolationType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageURL, setImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const { data: violationData } = await api.get(`/getViolation/${id}`);
        setViolationDetails(violationData);

        if (violationData.upload_urls) {
          const sanitizedFilename = encodeURIComponent(violationData.upload_urls.trim());
          setImageURL(`${API_BASE_URL}/violationsimages/${sanitizedFilename}`);
        }

        if (violationData.ccr_violation_type_id) {
          const { data: types } = await api.get(`/getCcrViolationTypes`);
          const type = types.find(t => t.id === violationData.ccr_violation_type_id);
          setViolationType(type || null);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!violationDetails) {
    return (
      <Typography color="error" sx={{ textAlign: "center", marginTop: 4 }}>
        Failed to load data. Please try again later.
      </Typography>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ marginBottom: 3 }}
      >
        Back
      </Button>

      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md">
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          <IconButton onClick={() => setOpenImageModal(false)} sx={{ position: "absolute", top: 8, right: 8 }}>
            <CloseIcon />
          </IconButton>
          <img
            src={imageURL}
            alt="Violation Evidence"
            style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>

      <Grid container spacing={3}>
        {/* Timeline (Left column) */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 2,
              position: { md: "sticky" },
              top: { md: 80 },
              height: "fit-content",
              boxShadow: 3,
            }}
          >
          <ViolationsTimeline violation_id={id} />
          </Card>
        </Grid>

        {/* Tabbed Content (Right column) */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                variant="fullWidth"
                sx={{ marginBottom: 3 }}
              >
                <Tab label="Details" />
                <Tab label="Comments" />
                <Tab label="Visits" />
              </Tabs>

              {activeTab === 0 && (
                <Box>
                  {imageURL && (
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                      <img
                        src={imageURL}
                        alt="Violation"
                        style={{ maxWidth: "200px", cursor: "pointer" }}
                        onClick={() => setOpenImageModal(true)}
                      />
                      <Typography variant="body2" color="textSecondary">Click to enlarge</Typography>
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <InfoIcon color="primary" />
                        <Typography><strong>Violation ID:</strong> VLT-{violationDetails.id}</Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CategoryIcon color="secondary" />
                          <Typography><strong>Type:</strong> {violationType?.title || "N/A"}</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {violationType?.resolution_step || "No description available."}
                        </Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <HomeIcon color="success" />
                        <Typography><strong>Property ID:</strong> {violationDetails.property_id || "N/A"}</Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <CalendarTodayIcon color="warning" />
                        <Typography><strong>Violated At:</strong> {new Date(violationDetails.violated_at).toLocaleString()}</Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <PersonIcon color="info" />
                        <Typography><strong>Cure By:</strong> {violationDetails.cure_by || "Unassigned"}</Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <CommentIcon color="primary" />
                        <Typography><strong>Comment:</strong> {violationDetails.comment || "N/A"}</Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <CheckCircleIcon color="success" />
                        <Typography><strong>Status:</strong></Typography>
                        <Chip
                          label={violationDetails.status}
                          color={
                            violationDetails.status === "resolved"
                              ? "success"
                              : violationDetails.status === "pending"
                              ? "warning"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <ReportProblemIcon color="error" />
                        <Typography><strong>Created At:</strong> {new Date(violationDetails.created_at).toLocaleString()}</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <ViolationComments violation_id={id} currentUserId={localStorage.getItem("currentUserId") || 1} />
              )}

              {activeTab === 2 && (
                <Visits violation_id={id} currentUserId={localStorage.getItem("currentUserId") || 1} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberViolationDetails;
